import {
  Fragment,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { markAsRead } from 'redux/actions/notifications';
import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  SvgIcon,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  BlurCircular as BlurCircularIcon,
  Error as ErrorIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsNone as NotificationsNoneIcon,
  NotificationImportant as NotificationImportantIcon,
} from '@material-ui/icons';
import { green, red } from '@material-ui/core/colors';

import { dateTimeFormat } from 'utils/formater';

const typeIcons = {
  default: BlurCircularIcon,
  error: ErrorIcon,
  notification: NotificationImportantIcon,
};

const typeColors = {
  error: 'icon_error',
  notification: 'icon_notification',
};

const getNotificationPath = (item) => {
  if (item?.message?.type === 'pld_denuncia') {
    return '/pld/reports/internal';
  }
  if (item?.message?.type === 'new_physical') {
    const physicalId = item?.message?.id;
    return physicalId ? `/physical_cards/details/${physicalId}` : '/physical_cards';
  }
  if (item?.message?.type === 'coincidencia_lpb') {
    const peopleId = item?.message?.id;
    return `/people/${peopleId}/pld#internal_query`;
  }
  return `/notifications/${item.id}`;
};

const useStyles = makeStyles((theme) => ({
  popover: {
    width: 320,
  },
  icon_default: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  icon_notification: {
    backgroundColor: green[700],
    color: theme.palette.secondary.contrastText,
  },
  icon_error: {
    backgroundColor: red[700],
    color: theme.palette.secondary.contrastText,
  },
}));

function Notifications() {
  const classes = useStyles();
  const { pendings } = useSelector(({ notifications }) => notifications);
  const dispatch = useDispatch();
  const history = useHistory();
  
  const { t } = useTranslation('translation', {
    keyPrefix: 'system.menu.notifications',
  });

  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNotificationClick = (item) => {
    dispatch(markAsRead(item.id));
    handleClose();
    history.push(getNotificationPath(item));
  };

  return (
    <>
      <Tooltip title={t('tooltip')}>
        <IconButton color="inherit" ref={ref} onClick={handleOpen}>
          <Badge badgeContent={pendings.total} color="default" overlap='rectangular'>
            <SvgIcon
              children={pendings.total
                ? <NotificationsActiveIcon />
                : <NotificationsNoneIcon />
              }
            />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        classes={{
          paper: classes.popover,
        }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <Box p={2}>
          <Typography
            children={t('title')}
            color="textPrimary"
            variant="h5"
          />
        </Box>
        {pendings.total === 0 ? (
          <Box p={2}>
            <Typography
              children={t('empty')}
              color="textPrimary"
              variant="h6"
            />
          </Box>
        ) : (
          <List className={classes.list} disablePadding>
            {pendings.rows.slice(0, 5).map((item, index) => {
              const Icon = typeIcons[item?.message?.alert_type || 'default'] || Fragment;
              return (
                <ListItem
                  button
                  className={classes.listItem}
                  divider
                  key={index}
                  onClick={() => handleNotificationClick(item)}
                >
                  <ListItemAvatar>
                    <Avatar className={classes[typeColors[item?.message?.alert_type] || 'icon_default']}>
                      <SvgIcon fontSize="small">
                        <Icon />
                      </SvgIcon>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={item?.message?.name}
                    primaryTypographyProps={{
                      variant: 'subtitle2',
                      color: 'textPrimary',
                    }}
                    secondary={dateTimeFormat(item.created_at, undefined, true)}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
        <Box p={1} display="flex" justifyContent="center">
          <Button
            children={t('show_more')}
            component={RouterLink}
            onClick={handleClose}
            size="small"
            to="/notifications"
          />
        </Box>
      </Popover>
    </>
  );
}

export default Notifications;
